import { useEffect } from "react";

import dayjs from "dayjs";
import PropTypes from "prop-types";
import { FormProvider, useForm } from "react-hook-form";

import { yupResolver } from "@hookform/resolvers/yup";
import { Stack } from "@mui/material";
import Grid from "@mui/material/Grid";

import { DiscountTypeEnum } from "../enums";
import {
  DEFAULT_VALUES,
  VALIDATION_SCHEMA,
} from "./promotion-form-constants";
import GeneralInformationSection from "./general-information-section";
import OutletAvailabilitySection from "./outlet-availability-section";
import UsageLimitsSection from "./usage-limits-section";
import ValiditySection from "./validity-section";

export const PromotionForm = ({ defaultValues, onSubmit, renderActionButton }) => {
  const methods = useForm({
    defaultValues: DEFAULT_VALUES,
    resolver: yupResolver(VALIDATION_SCHEMA),
  });
  const { reset, handleSubmit, formState } = methods;

  useEffect(() => {
    if (!defaultValues) {
      return;
    }
    reset({ ...DEFAULT_VALUES, ...defaultValues });
  }, [defaultValues, reset]);

  const onFormSubmit = async (data) => {
    try {
      const payload = {
        promotionCampaignId: data.promotionCampaign.id,
        name: data.name,
        promotionType: data.promotionType,
        discountType: data.discountType,
        discountAmount: data.discountAmount,
        // The field is hidden for fixed amounts but keeps its value, so clear it here.
        maximumDiscountAmount:
          data.discountType === DiscountTypeEnum.PERCENTAGE
            ? (data.maximumDiscountAmount ?? null)
            : null,
        validFrom: dayjs(data.validFrom).format("YYYY-MM-DD"),
        validTo: dayjs(data.validTo).format("YYYY-MM-DD"),
        status: data.status,
        globalUsageLimit: data.globalUsageLimit,
        perCustomerLimit: data.perCustomerLimit,
        outletIds: Object.keys(data.outlets).filter((key) => data.outlets[key]),
      };

      await onSubmit(payload);
    } catch (error) {
      console.log(error.message);
    }
  };

  return (
    <FormProvider {...methods}>
      <Stack
        component="form"
        noValidate
        onSubmit={handleSubmit(onFormSubmit)}
        spacing={3}
      >
        <GeneralInformationSection />
        <Grid container spacing={3}>
          <Grid size={{ xs: 12, md: 6 }}>
            <ValiditySection />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <UsageLimitsSection />
          </Grid>
        </Grid>
        <OutletAvailabilitySection />
        {renderActionButton({ isSubmitting: formState.isSubmitting })}
      </Stack>
    </FormProvider>
  );
};

PromotionForm.propTypes = {
  defaultValues: PropTypes.object,
  onSubmit: PropTypes.func.isRequired,
  renderActionButton: PropTypes.func.isRequired,
};
